import { HttpBackend, HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

interface UploadPartUrl {
  partNumber: number;
  url: string;
}

interface IniciarResultado {
  nome: string;
  key: string;
  uploadId: string;
  partUrls: UploadPartUrl[];
}

interface ParteConcluida {
  partNumber: number;
  eTag: string;
}

/**
 * Upload de arquivos direto ao S3 via URLs pré-assinadas (multipart).
 *
 * Segurança: a credencial S3 fica SOMENTE no servidor (Esteira), que assina as
 * URLs das partes. O navegador nunca vê a chave — apenas dá PUT do binário nas
 * URLs temporárias. Os bytes vão direto browser → S3, sem passar pela BFF/Esteira.
 *
 * Fluxo:
 *  1. POST .../Upload/Iniciar  -> { nome, uploadId, partUrls[] } (assinadas na Esteira)
 *  2. PUT de cada parte direto no S3 (paralelo, com retry e progresso)
 *  3. POST .../Upload/Concluir -> Esteira valida no S3 e persiste o documento
 *
 * Os PUTs ao S3 usam um HttpClient criado do HttpBackend, sem os interceptors da
 * aplicação — assim o Authorization (JWT) não é enviado ao *.amazonaws.com.
 */
@Injectable({ providedIn: 'root' })
export class UploadDiretoS3Service {
  private readonly s3Http: HttpClient;
  private readonly chunkBytes = 16 * 1024 * 1024; // 16 MB (>= 5 MB exigido pelo S3, exceto a última parte)
  private readonly concorrencia = 4;

  constructor(private http: HttpClient, handler: HttpBackend) {
    this.s3Http = new HttpClient(handler);
  }

  /** Upload direto de documento de PROPOSTA (endpoints Documentos/Proposta/...). Emite progresso 0-100. */
  enviar(file: File, propostaId: string, tipoDocumento: string, extensao: string): Observable<number> {
    const baseUrl = `${environment.API}/${environment.V1}/Documentos/Proposta/${propostaId}/Upload`;
    return this.executarUpload(baseUrl, file, tipoDocumento, extensao);
  }

  /** Upload de documento de PROPOSTA retornando documentoId ao concluir.
   *  Emite `{ progresso: 0-100, documentoId }` — documentoId presente apenas na emissão final (100). */
  enviarParaProposta(
    file: File,
    propostaId: string,
    tipoDocumento: string,
    extensao: string
  ): Observable<{ progresso: number; documentoId?: string }> {
    const baseUrl = `${environment.API}/${environment.V1}/Documentos/Proposta/${propostaId}/Upload`;
    return this.executarUploadComResultado(baseUrl, file, tipoDocumento, extensao);
  }

  /** Upload de documento do ESPELHO DE OFERTA (endpoints EspelhoOfertaDocumentos/EspelhoOferta/{id}/Upload/...).
   *  Emite `{ progresso: 0-100, documentoId }` — documentoId presente apenas na emissão final (100). */
  enviarParaEspelhoOferta(
    file: File,
    espelhoOfertaId: string,
    tipoDocumento: string,
    extensao: string
  ): Observable<{ progresso: number; documentoId?: string }> {
    const baseUrl = `${environment.API}/${environment.V1}/EspelhoOfertaDocumentos/EspelhoOferta/${espelhoOfertaId}/Upload`;
    return this.executarUploadComResultado(baseUrl, file, tipoDocumento, extensao);
  }

  /** Upload direto de documento de CADASTRO DO PARCEIRO (endpoints DadosComplementares/Parceiro/...). */
  enviarDocumentoParceiro(file: File, parceiroId: string, tipoDocumento: string | number, extensao: string): Observable<number> {
    const baseUrl = `${environment.API}/${environment.V1}/DadosComplementares/Parceiro/${parceiroId}/Upload`;
    return this.executarUpload(baseUrl, file, tipoDocumento, extensao);
  }

  /** Upload direto de documento de REPRESENTANTE (endpoints DadosComplementares/Representante/...).
   *  Emite `{ progresso: 0-100, documentoId }` — documentoId presente apenas na emissão final (100). */
  enviarDocumentoRepresentante(
    file: File,
    parceiroId: string,
    tipoDocumento: string | number,
    extensao: string
  ): Observable<{ progresso: number; documentoId?: string }> {
    const baseUrl = `${environment.API}/${environment.V1}/DadosComplementares/Representante/${parceiroId}/Upload`;
    return this.executarUploadComResultado(baseUrl, file, tipoDocumento, extensao);
  }

  private executarUploadComResultado(
    baseUrl: string,
    file: File,
    tipoDocumento: string | number,
    extensao: string
  ): Observable<{ progresso: number; documentoId?: string }> {
    return new Observable(subscriber => {
      let abortado = false;
      let init: IniciarResultado | undefined;

      const executar = async (): Promise<void> => {
        const partCount = Math.ceil(file.size / this.chunkBytes) || 1;

        init = await lastValueFrom(
          this.http.post<IniciarResultado>(`${baseUrl}/Iniciar`, {
            tipoDocumento,
            extensao,
            nomeExterno: String(tipoDocumento),
            contentType: file.type,
            partCount,
          })
        );

        const carregadoPorParte = new Map<number, number>();
        const concluidas: ParteConcluida[] = [];

        const emitirProgresso = (): void => {
          let total = 0;
          carregadoPorParte.forEach(v => (total += v));
          subscriber.next({ progresso: Math.min(99, Math.round((total / file.size) * 100)) });
        };

        const fila = [...init.partUrls];
        const worker = async (): Promise<void> => {
          while (fila.length && !abortado) {
            const { partNumber, url } = fila.shift()!;
            const inicio = (partNumber - 1) * this.chunkBytes;
            const blob = file.slice(inicio, Math.min(inicio + this.chunkBytes, file.size));
            const eTag = await this.enviarParteComRetry(url, blob, partNumber, carregadoPorParte, emitirProgresso);
            concluidas.push({ partNumber, eTag });
          }
        };

        await Promise.all(Array.from({ length: this.concorrencia }, () => worker()));

        await lastValueFrom(
          this.http.post(`${baseUrl}/Concluir`, {
            nome: init.nome,
            // Reenvia a key assinada no Iniciar para o backend completar o multipart
            // exatamente onde as partes foram gravadas (evita divergência de key -> NoSuchKey).
            key: init.key,
            extensao,
            tipoDocumento,
            nomeExterno: String(tipoDocumento),
            uploadId: init.uploadId,
            parts: concluidas.sort((a, b) => a.partNumber - b.partNumber),
          })
        );

        subscriber.next({ progresso: 100, documentoId: init.nome });
        subscriber.complete();
      };

      executar().catch(async erro => {
        if (init) {
          try {
            await lastValueFrom(
              this.http.post(`${baseUrl}/Abortar`, { nome: init.nome, key: init.key, extensao, uploadId: init.uploadId })
            );
          } catch { /* best-effort */ }
        }
        subscriber.error(erro);
      });

      return () => { abortado = true; };
    });
  }

  /**
   * Núcleo do upload direto ao S3 (multipart via URLs pré-assinadas) — compartilhado
   * pelos fluxos de proposta e de cadastro do parceiro. Emite o progresso de 0 a 100;
   * completa ao registrar o documento e, em erro, aborta o multipart.
   */
  private executarUpload(baseUrl: string, file: File, tipoDocumento: string | number, extensao: string): Observable<number> {
    return new Observable<number>(subscriber => {
      let abortado = false;
      let init: IniciarResultado | undefined;

      const executar = async (): Promise<void> => {
        const partCount = Math.ceil(file.size / this.chunkBytes) || 1;

        init = await lastValueFrom(
          this.http.post<IniciarResultado>(`${baseUrl}/Iniciar`, {
            tipoDocumento,
            extensao,
            nomeExterno: String(tipoDocumento),
            contentType: file.type,
            partCount,
          })
        );

        const carregadoPorParte = new Map<number, number>();
        const concluidas: ParteConcluida[] = [];

        const emitirProgresso = (): void => {
          let total = 0;
          carregadoPorParte.forEach(v => (total += v));
          subscriber.next(Math.min(99, Math.round((total / file.size) * 100)));
        };

        const fila = [...init.partUrls];
        const worker = async (): Promise<void> => {
          while (fila.length && !abortado) {
            const { partNumber, url } = fila.shift()!;
            const inicio = (partNumber - 1) * this.chunkBytes;
            const blob = file.slice(inicio, Math.min(inicio + this.chunkBytes, file.size));
            const eTag = await this.enviarParteComRetry(url, blob, partNumber, carregadoPorParte, emitirProgresso);
            concluidas.push({ partNumber, eTag });
          }
        };

        await Promise.all(Array.from({ length: this.concorrencia }, () => worker()));

        await lastValueFrom(
          this.http.post(`${baseUrl}/Concluir`, {
            nome: init.nome,
            // Reenvia a key assinada no Iniciar para o backend completar o multipart
            // exatamente onde as partes foram gravadas (evita divergência de key -> NoSuchKey).
            key: init.key,
            extensao,
            tipoDocumento,
            nomeExterno: String(tipoDocumento),
            uploadId: init.uploadId,
            parts: concluidas.sort((a, b) => a.partNumber - b.partNumber),
          })
        );

        subscriber.next(100);
        subscriber.complete();
      };

      executar().catch(async erro => {
        if (init) {
          // libera as partes no S3 para não deixar upload órfão (também coberto por lifecycle).
          try {
            await lastValueFrom(
              this.http.post(`${baseUrl}/Abortar`, {
                nome: init.nome,
                key: init.key,
                extensao,
                uploadId: init.uploadId,
              })
            );
          } catch {
            /* aborto best-effort */
          }
        }
        subscriber.error(erro);
      });

      return () => {
        abortado = true;
      };
    });
  }

  private enviarParteComRetry(
    url: string,
    blob: Blob,
    partNumber: number,
    carregadoPorParte: Map<number, number>,
    emitirProgresso: () => void,
    tentativas = 3
  ): Promise<string> {
    const tentar = (): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        this.s3Http.put(url, blob, { observe: 'events', reportProgress: true }).subscribe({
          next: event => {
            if (event.type === HttpEventType.UploadProgress) {
              carregadoPorParte.set(partNumber, event.loaded);
              emitirProgresso();
            } else if (event.type === HttpEventType.Response) {
              const eTag = event.headers.get('ETag');
              if (!eTag) {
                reject(new Error('ETag ausente na resposta do S3 — verifique ExposeHeaders no CORS do bucket.'));
              } else {
                resolve(eTag);
              }
            }
          },
          error: reject,
        });
      });

    const executarComRetry = async (): Promise<string> => {
      for (let i = 1; i <= tentativas; i++) {
        try {
          return await tentar();
        } catch (e) {
          carregadoPorParte.set(partNumber, 0);
          emitirProgresso();
          if (i === tentativas) {
            throw e;
          }
          await new Promise(r => setTimeout(r, 500 * Math.pow(2, i)));
        }
      }
      throw new Error('Falha inesperada no envio da parte.');
    };

    return executarComRetry();
  }
}
